import type { Bill, BillUpdateRequestBody } from "@/services/core-api/types"
import { HttpError } from "@/utils/api"
import { getErrorNotificationKey } from "@/utils/notification-bridge"

import type { EditBillSchema } from "./edit-bill.schema"

export const billToFormValues = (bill: Bill): EditBillSchema => ({
  startDate: bill.start_date ?? "",
  endDate: bill.end_date ?? "",
  usage: bill.usage ?? "",
  usageUnit: bill.usage_unit ?? "",
  cost: bill.cost ?? "",
  currency: bill.currency ?? "",
  changeReason: "",
})

export const emptyFormValues: EditBillSchema = {
  startDate: "",
  endDate: "",
  usage: "",
  usageUnit: "",
  cost: "",
  currency: "",
  changeReason: "",
}

export const buildBillUpdates = (
  values: EditBillSchema,
  billDetails: Bill
): BillUpdateRequestBody => ({
  usage: values.usage,
  usage_unit: values.usageUnit,
  cost: values.cost === "" ? null : values.cost,
  currency:
    values.currency === ""
      ? null
      : (values.currency as BillUpdateRequestBody["currency"]),
  change_reason: values.changeReason.trim(),
  ...(values.startDate !== (billDetails.start_date ?? "")
    ? { start_date: values.startDate }
    : {}),
  ...(values.endDate !== (billDetails.end_date ?? "")
    ? { end_date: values.endDate }
    : {}),
})

type BillFieldSnapshot = Pick<
  EditBillSchema,
  "startDate" | "endDate" | "usage" | "usageUnit" | "cost" | "currency"
>

export const toBillFieldSnapshot = (
  billDetails: Bill | null | undefined
): BillFieldSnapshot => ({
  startDate: billDetails?.start_date ?? "",
  endDate: billDetails?.end_date ?? "",
  usage: billDetails?.usage ?? "",
  usageUnit: billDetails?.usage_unit ?? "",
  cost: billDetails?.cost ?? "",
  currency: billDetails?.currency ?? "",
})

export const hasBillFieldsChanged = (
  values: BillFieldSnapshot,
  snapshot: BillFieldSnapshot
): boolean =>
  values.startDate !== snapshot.startDate ||
  values.endDate !== snapshot.endDate ||
  values.usage !== snapshot.usage ||
  values.usageUnit !== snapshot.usageUnit ||
  values.cost !== snapshot.cost ||
  values.currency !== snapshot.currency

const getSubmitDomainErrorKey = (error: unknown): string => {
  if (!(error instanceof HttpError)) {
    return "editBill.updateBillError"
  }

  if (error.status === 409) {
    return "editBill.lockedEditError"
  }

  if (error.status === 404) {
    return "editBill.notFoundError"
  }

  if (error.status !== 422) {
    return "editBill.updateBillError"
  }

  if (error.message.includes("conflicts with an existing bill")) {
    return "editBill.dateConflictError"
  }

  if (error.message.includes("end_date must be on or after start_date")) {
    return "validation.invalidEndDate"
  }

  return "editBill.validationError"
}

export const getSubmitErrorKey = (error: unknown): string =>
  getErrorNotificationKey(error, getSubmitDomainErrorKey(error))
